export default function PrivacyPolicy() {
  return (
    <div className="pp-wrap">
      <div className="pp-inner">
        <a className="pp-back" href="/">← Back to sold4you</a>

        <h1 className="pp-title">Privacy Policy</h1>
        <p className="pp-updated">Last updated: 7th July, 2026</p>
        <p className="pp-lead">
          sold4you.app ("we," "us," "our," "the App") is committed to protecting your privacy.
          This Privacy Policy explains what information we collect, how we use it, how it's stored,
          and your rights regarding your information.
        </p>

        <Section n="1" title="Information We Collect">
          <p><strong>Account information.</strong> When you register, we collect your email address and a securely hashed password. We never store your password in plain text.</p>
          <p><strong>Location information.</strong> To help generate and localise your marketplace listings, we ask for your suburb and postcode. This information is stored locally on your own device using your browser's localStorage, not on our servers, unless you take an action that requires us to include it in a listing (for example, posting to Facebook Marketplace).</p>
          <p><strong>Listing content.</strong> Any photos, descriptions, prices, and other details you provide about items you're listing.</p>
          <p><strong>Payment information.</strong> Subscription payments are processed by Stripe, a third-party payment provider. We do not collect or store your full card details ourselves — Stripe handles this in accordance with its own privacy policy and security standards.</p>
          <p><strong>Usage information.</strong> Basic information about how you use the App, such as the number of listings created, used to manage free trial limits and improve the App.</p>
        </Section>

        <Section n="2" title="How We Use Your Information">
          <p>We use the information we collect to:</p>
          <ul>
            <li>Generate listing titles, descriptions, and processed images using AI-assisted tools</li>
            <li>Process your subscription payments</li>
            <li>Post your listings to Facebook Marketplace (or other supported platforms) at your request</li>
            <li>Provide, maintain, and improve the App</li>
            <li>Communicate with you about your account or the App</li>
          </ul>
        </Section>

        <Section n="3" title="How Your Information Is Stored and Processed">
          <ul>
            <li>Location data (suburb/postcode) is stored locally on your device via localStorage and is not transmitted to or stored on our servers unless required to complete an action you've requested, such as publishing a listing.</li>
            <li>Account and listing data is stored securely on our servers.</li>
            <li>Images you upload are stored using cloud-based object storage so they remain accessible for your past listings.</li>
            <li>Photos and item details you submit for listing generation are sent to third-party AI service providers (currently OpenAI and Photoroom) to generate listing text and process images (such as background removal). These providers process this data under their own privacy policies and only receive what's needed to perform this function — we do not send them your account password or payment details.</li>
          </ul>
        </Section>

        <Section n="4" title="Third-Party Services We Use">
          <p>We rely on the following third-party services to operate the App. Each receives only the information necessary to perform its specific function:</p>
          <div className="pp-table-wrap">
            <table className="pp-table">
              <thead>
                <tr><th>Service</th><th>Purpose</th><th>Data shared</th></tr>
              </thead>
              <tbody>
                <tr><td>OpenAI</td><td>Generating listing titles/descriptions</td><td>Photos and item details you submit for listing</td></tr>
                <tr><td>Photoroom</td><td>Image background processing</td><td>Photos you submit for listing</td></tr>
                <tr><td>Stripe</td><td>Payment processing</td><td>Payment and billing details (handled directly by Stripe)</td></tr>
                <tr><td>Facebook Marketplace</td><td>Publishing your listings</td><td>Listing content and location, at your request</td></tr>
                <tr><td>Replit</td><td>App hosting and infrastructure</td><td>Account and listing data as needed to run the App</td></tr>
              </tbody>
            </table>
          </div>
          <p>We encourage you to review these providers' own privacy policies for further detail on how they handle data.</p>
        </Section>

        <Section n="5" title="We Do Not Sell Your Data">
          <p>We do not sell, rent, or trade your personal information to third parties for marketing or advertising purposes. Information is only shared with the third-party services listed above, and only as needed to provide the App's functionality.</p>
        </Section>

        <Section n="6" title="Data Retention">
          <p>We retain your account and listing information for as long as your account remains active. If you delete your account, we will delete or anonymise your personal information within 30 days, except where we're required to retain certain information for legal, accounting, or security purposes.</p>
        </Section>

        <Section n="7" title="Your Rights">
          <p>Depending on where you live, you may have rights to:</p>
          <ul>
            <li>Access the personal information we hold about you</li>
            <li>Correct inaccurate information</li>
            <li>Request deletion of your information</li>
            <li>Object to or restrict certain processing</li>
          </ul>
          <p>To exercise any of these rights, contact us at <a href="mailto:support@sold4you.app">support@sold4you.app</a>.</p>
        </Section>

        <Section n="8" title="Children's Privacy">
          <p>The App is not directed at children, and we do not knowingly collect personal information from anyone under the age of 18. If you believe a child has provided us with personal information, please contact us so we can remove it.</p>
        </Section>

        <Section n="9" title="Cookies and Local Storage">
          <p>We use your browser's local storage to save certain preferences and information (such as your suburb/postcode) directly on your device, so it doesn't need to be re-entered each time you use the App. This information stays on your device and is not accessible to us unless you take an action that shares it, such as publishing a listing.</p>
        </Section>

        <Section n="10" title="Security">
          <p>We take reasonable technical and organisational measures to protect your information, including secure password hashing and encrypted connections. However, no method of transmission or storage is 100% secure, and we cannot guarantee absolute security.</p>
        </Section>

        <Section n="11" title="Changes to This Policy">
          <p>We may update this Privacy Policy from time to time. We'll update the "Last updated" date at the top of this page when we do, and where changes are significant, we'll aim to notify you directly.</p>
        </Section>

        <Section n="12" title="Contact Us">
          <p>If you have questions about this Privacy Policy or how your information is handled, contact us at:</p>
          <p><a href="mailto:support@sold4you.app">support@sold4you.app</a></p>
        </Section>
      </div>

      <style>{`
        *, *::before, *::after { box-sizing: border-box; }

        .pp-wrap {
          min-height: 100vh;
          background: #f9faf8;
          padding: 40px 20px 80px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          color: #1a1a1a;
        }

        .pp-inner {
          max-width: 720px;
          margin: 0 auto;
        }

        .pp-back {
          display: inline-block;
          margin-bottom: 32px;
          font-size: 14px;
          color: #4a7c59;
          text-decoration: none;
          font-weight: 500;
        }
        .pp-back:hover { text-decoration: underline; }

        .pp-title {
          font-size: 28px;
          font-weight: 600;
          margin: 0 0 6px;
          color: #111;
        }

        .pp-updated {
          font-size: 13px;
          color: #888;
          margin: 0 0 20px;
        }

        .pp-lead {
          font-size: 15px;
          line-height: 1.7;
          color: #444;
          margin-bottom: 36px;
          padding-bottom: 24px;
          border-bottom: 1px solid #e5e7e3;
        }

        .pp-section { margin-bottom: 32px; }

        .pp-section-title {
          font-size: 17px;
          font-weight: 600;
          color: #111;
          margin: 0 0 12px;
          padding-bottom: 8px;
          border-bottom: 1px solid #eee;
        }

        .pp-section p {
          font-size: 14px;
          line-height: 1.75;
          color: #444;
          margin: 0 0 12px;
        }

        .pp-section ul {
          padding-left: 20px;
          margin: 0 0 12px;
        }

        .pp-section li {
          font-size: 14px;
          line-height: 1.75;
          color: #444;
          margin-bottom: 6px;
        }

        .pp-section a {
          color: #4a7c59;
          text-decoration: underline;
          text-underline-offset: 2px;
        }

        .pp-table-wrap {
          overflow-x: auto;
          margin: 12px 0 16px;
          border-radius: 8px;
          border: 1px solid #e0e4dc;
        }

        .pp-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }

        .pp-table th {
          background: #f1f4ef;
          padding: 10px 14px;
          text-align: left;
          font-weight: 600;
          color: #333;
          border-bottom: 1px solid #dde1d9;
        }

        .pp-table td {
          padding: 10px 14px;
          color: #444;
          border-bottom: 1px solid #eee;
          vertical-align: top;
          line-height: 1.5;
        }

        .pp-table tr:last-child td { border-bottom: none; }
        .pp-table tr:nth-child(even) td { background: #fafbf9; }
      `}</style>
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <div className="pp-section">
      <h2 className="pp-section-title">{n}. {title}</h2>
      {children}
    </div>
  );
}
